<script lang="ts" setup>
/**
 * AURA Filters - Filter panel using UiRightPanel pattern from CallList
 * SRP: Only responsible for filter panel UI and data loading
 * Logic delegated to useAuraFilters composable
 */
import { onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { FilterIcon } from '@heroicons/vue/solid';

import { useAuraFilters } from '@/composables/useAuraFilters';
import { SelectOptionsService } from '@/services';
import type {
    SelectOptionsServiceGetUsersResult,
    SelectOptionsServiceGetQueuesResult,
    SelectOptionsServiceGetMediasResult,
} from '@/types';

const { t } = useI18n();
const {
    filterOpened,
    draftFilters,
    hasActiveFilters,
    activeFilterCount,
    applyFilters,
    clearFilters,
} = useAuraFilters();

// Loading states
const isLoadingUsers = ref(false);
const isLoadingQueues = ref(false);
const isLoadingMedias = ref(false);

// Options data
const users = ref<SelectOptionsServiceGetUsersResult[]>([]);
const queues = ref<SelectOptionsServiceGetQueuesResult[]>([]);
const medias = ref<SelectOptionsServiceGetMediasResult[]>([]);

// Load options on mount
onMounted(async () => {
    await Promise.all([loadUsers(), loadQueues(), loadMedias()]);
});

async function loadUsers() {
    isLoadingUsers.value = true;
    try {
        const response = await SelectOptionsService.getUsers();
        users.value = response.data;
    } catch (error) {
        console.error('Failed to load users:', error);
    } finally {
        isLoadingUsers.value = false;
    }
}

async function loadQueues() {
    isLoadingQueues.value = true;
    try {
        const response = await SelectOptionsService.getQueues();
        queues.value = response.data;
    } catch (error) {
        console.error('Failed to load queues:', error);
    } finally {
        isLoadingQueues.value = false;
    }
}

async function loadMedias() {
    isLoadingMedias.value = true;
    try {
        const response = await SelectOptionsService.getMedias();
        medias.value = response.data;
    } catch (error) {
        console.error('Failed to load medias:', error);
    } finally {
        isLoadingMedias.value = false;
    }
}
</script>

<template>
    <UiRightPanel v-model="filterOpened" :title="t('aura.filters.filter')">
        <template #button>
            <button
                class="aura-filters-button flex items-center gap-2 rounded-[10px] border border-[#D4D4D4] bg-white px-3 py-2 hover:bg-gray-50"
                @click="filterOpened = true"
            >
                <FilterIcon class="h-4 w-4 text-[#737373]" aria-hidden="true" />
                <span class="text-sm font-medium text-[#404040]">{{
                    t('aura.filters.filter')
                }}</span>
                <span
                    v-if="hasActiveFilters"
                    class="ml-1 rounded-full bg-[#48DDC3] px-2 py-0.5 text-xs text-white"
                >
                    {{ activeFilterCount }}
                </span>
            </button>
        </template>

        <template #default>
            <form autocomplete="off" class="flex flex-col gap-4">
                <!-- Operator -->
                <div>
                    <label class="mb-1 block text-sm font-medium text-gray-800">
                        {{ t('aura.filters.operator') }}
                    </label>
                    <UiMultiselect
                        v-model="draftFilters.operatorId"
                        name="operator"
                        :options="users"
                        label="name"
                        value-prop="id"
                        can-clear
                        :placeholder="t('aura.filters.selectOperator')"
                        :loading="isLoadingUsers"
                        searchable="local"
                        :allow-empty="true"
                        :show-labels="false"
                    />
                </div>

                <!-- Queue -->
                <div>
                    <label class="mb-1 block text-sm font-medium text-gray-800">
                        {{ t('aura.filters.queue') }}
                    </label>
                    <UiMultiselect
                        v-model="draftFilters.queueId"
                        name="queue"
                        :options="queues"
                        label="name"
                        value-prop="id"
                        can-clear
                        :placeholder="t('aura.filters.selectQueue')"
                        :loading="isLoadingQueues"
                        searchable="local"
                        :allow-empty="true"
                        :show-labels="false"
                    />
                </div>

                <!-- Media (Number Media) -->
                <div>
                    <label class="mb-1 block text-sm font-medium text-gray-800">
                        {{ t('aura.filters.media') }}
                    </label>
                    <UiMultiselect
                        v-model="draftFilters.mediaId"
                        name="media"
                        :options="medias"
                        label="label"
                        value-prop="value"
                        :primitive="true"
                        can-clear
                        :placeholder="t('aura.filters.selectMedia')"
                        :loading="isLoadingMedias"
                        searchable="local"
                        :allow-empty="true"
                        :show-labels="false"
                    />
                </div>

                <!-- Action Buttons -->
                <div class="mt-6 grid grid-cols-2 gap-3">
                    <UiButton variant="white" text-variant="gray-700" @click="clearFilters()">
                        {{ t('aura.filters.clear') }}
                    </UiButton>
                    <UiButton variant="primary" text-variant="white" @click="applyFilters()">
                        {{ t('core.actions.Apply') }}
                    </UiButton>
                </div>
            </form>
        </template>
    </UiRightPanel>
</template>

<style scoped>
@media (max-width: 767px) {
    .aura-filters-button {
        gap: 0.375rem;
        padding: 0.375rem 0.625rem;
    }

    .aura-filters-button span {
        font-size: 0.75rem;
    }
}

@media (orientation: landscape) and (max-height: 500px) {
    .aura-filters-button {
        gap: 0.375rem;
        padding: 0.375rem 0.625rem;
    }

    .aura-filters-button span {
        font-size: 0.75rem;
    }
}
</style>
